import React, { useEffect, useRef, useState } from 'react';
import './PlaylistsPage.css';
import './SupportPage.css';

import mobileNoise from './bg_noise/mobile_noise.gif';
import tabletNoise from './bg_noise/tablet_noise.gif';
import desktopNoise from './bg_noise/desktop_noise.gif';
import './osci-logo-engine-v26-transparent';
import { LOGO_SEQUENCE, OSCI_SVG_ASSET_URLS, getInitialLogoVisualIndexForPage } from './logoVisuals';
import { DecodedArtImage, ResponsiveDecodedBackground } from './components/DecodedVisuals';

import spotifyIcon from './streaming_icons/Spotify.svg';
import appleIcon from './streaming_icons/Apple.svg';
import youtubeIcon from './streaming_icons/Youtube.svg';

import instagramIcon from './soc icons/1_icons8-instagram.svg';
import spotifyProfileIcon from './soc icons/2_icons8-spotify.svg';
import youtubeSocialIcon from './soc icons/3_icons8-youtube.svg';
import pinterestIcon from './soc icons/5_icons8-pinterest.svg';
import homeIcon from './soc icons/0_home.svg';
import { applyRandomFlicker, decodeTextLocked, runAfterFontsReady } from './utils/textEffects';
import {
  formatFollowerDisplayCount,
  getFollowerDisplayCount,
  getPlaylistFollowerCount,
} from './data/playlistFollowers';
import { handleSubmitMusicIntentClick } from './metaPixel';
import { trackSiteEvent } from './siteAnalytics';
import { useRegionalVisibility } from './regionalVisibility';
import {
  PUBLIC_SUBMISSION_REL,
  PUBLIC_SUBMISSION_TARGET,
  PUBLIC_SUBMISSION_URL,
  SUBMITHUB_DIRECT_TEMPORARY_MODE,
} from './paymentMaintenance';

const DEFAULT_HERO_GIF = `${process.env.PUBLIC_URL}/static/img/Skull_scanline.gif`;

const STREAMING_META = {
  spotify: { label: 'Spotify', icon: spotifyIcon },
  apple: { label: 'Apple Music', icon: appleIcon },
  youtube: { label: 'YouTube Music', icon: youtubeIcon },
};

const PLAYLIST_DEFINITIONS = [
  {
    id: 'dark-side',
    emoji: '🌘',
    name: 'DARK SIDE',
    vibe: 'OBSIDIAN STORM',
    tagline:
      "Only for the tough ones. You say what you think and do what you want – welcome to the dark side.",
    info: [
      {
        label: 'Essence',
        text:
          "It's all about dark vibes and heavy energy. This is where pink dreams drown in the grim waters of reality. Only bones and flesh.",
      },
      { label: 'Genres', text: 'Dark trap, phonk, trap metal, scream rap.' },
      {
        label: 'Not here',
        text:
          'Sappy, romantic, whiny, or happy tracks. Basic tracks following overused genre templates. Mixes with harsh and uncomfortable sounds.',
      },
      { label: 'Warnings', text: 'Not for softies or easily offended.' },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/44chMC8qDiVyiwjzmZio0Y?si=gycACwZhR-it2ylmudNAnw&pi=vFVrxvRMSWO58' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/dark-side/pl.u-AkAmPlyU2bDqJ0e' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-8L58hJayLuGA651advSf0p&si=wIqpemAErWLNK-me' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/44chMC8qDiVyiwjzmZio0Y?utm_source=generator&theme=0',
  },
  {
    id: 'fresh-blood',
    emoji: '🩸',
    name: 'FRESH BLOOD',
    vibe: 'RARE SIGNALS',
    tagline:
      'For anyone tired of the same old beats and looking for real inspiration. Unique tracks pushing the boundaries belong here.',
    info: [
      {
        label: 'Essence',
        text:
          'Refresh with these new jams if you feel stuck and getting moldy – this is the spot for tracks that reset your brain.',
      },
      {
        label: 'Genres',
        text: 'From cloud rap, vapor trap to glitchcore, sigilkore to post-punk.',
      },
      {
        label: 'Not here',
        text: 'Basic tracks following overused genre templates. Mixes with harsh and uncomfortable sounds.',
      },
      { label: 'Warnings', text: 'Not for those stuck in the past.' },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/25Ry369evgzmi7yAimZ3IY?si=7PJGxjitTESbUR6gbCMv_g&pi=NH60hYqgSkeAA' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/fresh-blood/pl.u-b3b8VdeUy20BW67' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_--qCHyNG2umIpLtRoLcVMZH&si=puboqtcVhonnxIgP' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/25Ry369evgzmi7yAimZ3IY?utm_source=generator&theme=0',
  },
  {
    id: 'diamonds-bullets',
    emoji: '💰',
    name: 'DIAMONDS & BULLETS',
    vibe: 'HIGH VOLTAGE',
    tagline:
      "Dope tracks that hit hard but don't quite fit my other playlists. Mostly high-energy bangers.",
    info: [
      {
        label: 'Essence',
        text: 'So get charged up here if you\'re feeling burned out – it\'s a stash of peak-time adrenaline.',
      },
      { label: 'Genres', text: 'Drill, jersey club, trap, hip-hop.' },
      {
        label: 'Not here',
        text: 'Slow, boring, chill stuff. Mixes with harsh, uncomfortable sounds.',
      },
      { label: 'Warnings', text: "If you hate drill...this ain't for you." },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/17PGwbqjUBbJnAoceiOqrO?si=5lr0ftiDQN6ASqsUOTcbrA&pi=qUlEd-JtQzWAM' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/diamonds-bullets/pl.u-RRbVvlxI3JZYALM' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-9Fyd1Iw1ejvMVRvtfjU8TZ&si=4rFNcv8WDEP6e837' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/17PGwbqjUBbJnAoceiOqrO?utm_source=generator&theme=0',
  },
  {
    id: 'private-paradise',
    emoji: '💊',
    name: 'PRIVATE PARADISE',
    vibe: 'ALT SERENITY',
    tagline:
      "A chill playlist, but don't think it's all peace and quiet. Expect deep emotions and mixed vibes.",
    info: [
      {
        label: 'Essence',
        text: "This music gives a rich experience for those who don't see the world in black and white.",
      },
      {
        label: 'Genres',
        text: 'Lo-fi ambient, dream punk, vaporwave, chill phonk, cloud rap, vapor trap.',
      },
      {
        label: 'Not here',
        text: 'Jazzed-up lo-fi hip-hop, flat mood tracks, 10-minute pad tracks, harsh and uncomfortable mixes.',
      },
      { label: 'Warnings', text: "Nope, this playlist ain't for yoga and meditation." },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/1ZuXbfzuinPKrse2Si8vaK?si=u2NnMFqLQPKUFZSZgmSrIQ&pi=pSMxAfvxTBmUt' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/private-paradise/pl.u-leyl0yeCj79rV1G' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_--UBgjD3WImryrS1N6oH5Co&si=3PFK-_YyGuXJtk80' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/1ZuXbfzuinPKrse2Si8vaK?utm_source=generator&theme=0',
  },
  {
    id: 'infinite-dreams',
    emoji: '🌙',
    name: 'INFINITE DREAMS',
    vibe: 'CINEMATIC NOSTALGIA',
    tagline:
      "Get ready to dive deep into your most dramatic feels. Major nostalgia for days lived and unlived guaranteed.",
    info: [
      {
        label: 'Essence',
        text: "Seriously, don't hit play unless you're down for some serious tears.",
      },
      { label: 'Genres', text: 'Post-punk, shoegaze, dream pop.' },
      {
        label: 'Not here',
        text: 'Uplifting, feel-good, chill vibes and any mixes with harsh, jarring sounds.',
      },
      {
        label: 'Warnings',
        text: 'If you do not want your day to turn monochrome with a heavy grain filter, save it for later.',
      },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/32py1335U0YhsYb08xpRmi?si=B6SuoP3eR2GZahgcTHUByA&pi=I6B6RwzKT9G97' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/infinite-dreams/pl.u-vxy693XCzE4Zad3' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-9dLVuwrqcOjtsyWVWlx_Zp&si=QgVxKoFjgFFCyueT' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/32py1335U0YhsYb08xpRmi?utm_source=generator&theme=0',
  },
  {
    id: 'uncontrollable',
    emoji: '⛓',
    name: 'UNCONTROLLABLE',
    vibe: 'MOSH MODE',
    tagline:
      'Your wild ride starts here. Rage and fury, betrayal and revenge, love and hate – dive into this insane mix.',
    info: [
      {
        label: 'Essence',
        text: 'Let loose in the mosh pit and rediscover yourself with busted knuckles, walls, and feelings.',
      },
      { label: 'Genres', text: 'Metalcore, nu-metal, post-hardcore, punk.' },
      {
        label: 'Not here',
        text: 'Too melodic, overly lyrical tracks, old-school vibes, basic tracks, no self-harm sounds.',
      },
      { label: 'Warnings', text: "This playlist won't soothe you, it'll turn your heart into metal." },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/2uLDhw8LY6vmKatUDC0QO6?si=uponwNhfQIOwx6XfQsh0ww&pi=QlqME17qSwSMs' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/uncontrollable/pl.u-AkAmPNbF2bDqJ0e' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-9_xVLjGVumpxsitKcF4alQ&si=pmPYs445BA0snGxJ' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/2uLDhw8LY6vmKatUDC0QO6?utm_source=generator&theme=0',
  },
  {
    id: 'beach-hype',
    emoji: '♨️',
    name: 'BEACH HYPE',
    vibe: 'SUNSET CHARGE',
    tagline: 'The hottest tracks from places where the sun never quits and the night parties get even hotter.',
    info: [
      {
        label: 'Essence',
        text: 'Tuned for sand-in-your-shoes energy, from day heat to neon-lit boardwalks.',
      },
      {
        label: 'Genres',
        text: 'Brazilian phonk, reggaeton, dembow, amapiano, jersey club, afrobeat.',
      },
      {
        label: 'Not here',
        text: 'Happy, lovey-dovey vibes, too traditional stuff, mixes with obvious sound issues.',
      },
      { label: 'Warnings', text: "If you ain't vibin'... maybe it's bedtime." },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/6nIJ0AhZB3pmWfBLNDOB6O?si=FS_L_zDcTCSKorPyEpDQxg&pi=sT-b4RuQSlWz9' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/beach-hype/pl.u-b3b8VGGCy20BW67' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-9ujdmhSJTdbb-Lco9Lo36M&si=oi_1AC1nmXYBLe1B' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/6nIJ0AhZB3pmWfBLNDOB6O?utm_source=generator&theme=0',
  },
  {
    id: 'dance-or-die',
    emoji: '💀',
    name: 'DANCE OR DIE',
    vibe: 'NIGHTLIFE HEAT',
    tagline:
      "Remember that one party where you went wild and every other party after that felt lame? This brings it back.",
    info: [
      {
        label: 'Essence',
        text: 'These tracks will make you move – I dug through the underground so you do not have to.',
      },
      { label: 'Genres', text: 'Bass house, speed house, electro.' },
      {
        label: 'Not here',
        text: "Tracks that could play in a café, or drops that don't make you ditch your drink and hit the floor.",
      },
      {
        label: 'Warnings',
        text: "Don't listen while driving – you're bound to get a speeding ticket.",
      },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/4UrExGSO60rP6QtOjapAvs?si=QABAt6OZS3qXcAt_WM4uCw' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/dance-or-die/pl.u-RRbVv1Wu3JZYALM' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_--cv6FoyzPNj5xtWiijNScs&si=BpMXrOUwPWS7RY4f' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/4UrExGSO60rP6QtOjapAvs?utm_source=generator&theme=0',
  },
  {
    id: 'nitro-go',
    emoji: '🔋',
    name: 'NITRO GO!',
    vibe: 'BOOSTED BPM',
    tagline:
      'The most powerful energy drink out there. For those who want a serious power boost. Gamers will love it.',
    info: [
      {
        label: 'Essence',
        text: "Buckle up, it's gonna be a wild ride – hyper-focused on velocity and bass weight.",
      },
      { label: 'Genres', text: 'Neuro funk, dnb, halftime dnb, liquid bass.' },
      {
        label: 'Not here',
        text: 'Tracks with weak bass, soft drops, romantic or lyrical parts, tracks that physically hurt to listen to.',
      },
      { label: 'Warnings', text: 'Rapid heartbeat.' },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/3U0GRcFDcHZ8SWHUMb27tQ?si=jDPHSrrFQse1Qnlk7-p0sg' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/nitro-go/pl.u-leyl0Y6tj79rV1G' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-81kBx13sCqsag5wq245U_w&si=PvrhTp3n_Y834bR7' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/3U0GRcFDcHZ8SWHUMb27tQ?utm_source=generator&theme=0',
  },
  {
    id: 'trippy-glitch',
    emoji: '👾',
    name: 'TRIPPY GLITCH',
    vibe: 'ALIEN SIGNALS',
    tagline:
      'Looking for some fresh vibes? These tracks are literally out of this world. Forget the tinfoil hat and plug in.',
    info: [
      {
        label: 'Essence',
        text: 'Found during a cosmic scavenger hunt – glitchy, bright, and ready to warp reality.',
      },
      { label: 'Genres', text: 'Glitchcore, glitchbreak, sigilkore, hyperpop.' },
      {
        label: 'Not here',
        text: 'Tracks that just copy alien music, mixes that are impossible for humans to hear.',
      },
      { label: 'Warnings', text: 'Only listen if you are ready for contact. Reality might get warped.' },
    ],
    streaming: [
      { type: 'spotify', url: 'https://open.spotify.com/playlist/3D5cDZl0HlKwYTh9FK4Kmx?si=QFLhwMxWRgOLFGLSothfig' },
      { type: 'apple', url: 'https://music.apple.com/ph/playlist/trippy-glitch/pl.u-RRbVvBVF3JZYALM' },
      { type: 'youtube', url: 'https://music.youtube.com/playlist?list=PLbZ3ZnkrQ_-_xOHyFDxMnisrmnwJVx73J&si=k2O42WJGhcMGrR9z' },
    ],
    embedSrc:
      'https://open.spotify.com/embed/playlist/3D5cDZl0HlKwYTh9FK4Kmx?utm_source=generator&theme=0',
  },
];

const PLAYLISTS = PLAYLIST_DEFINITIONS.map((playlist) => ({
  ...playlist,
  followers: getPlaylistFollowerCount(playlist.id),
}));

const PLAYLIST_EMOJI_DECODE_OFFSETS = [
  0,
  -1450,
  420,
  -760,
  -1880,
  -280,
  -1120,
  260,
  -1620,
  -540,
];

const PLAYLIST_PLAYER_MAX_RETRIES = 3;
const PLAYLIST_PLAYER_LOAD_TIMEOUT_MS = 5200;

function buildPlaylistEmbedSrc(src, attempt) {
  if (!attempt) {
    return src;
  }

  try {
    const url = new URL(src);
    url.searchParams.set('ivg_retry', String(attempt));
    return url.toString();
  } catch {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}ivg_retry=${attempt}`;
  }
}

function runWhenPageVisible(callback) {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
    callback();
    return () => {};
  }

  let didRun = false;
  let timeoutId;

  const run = () => {
    if (didRun) return;
    didRun = true;
    window.removeEventListener('pageshow', run);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    clearTimeout(timeoutId);
    callback();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'hidden') {
      run();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pageshow', run, { once: true });
  timeoutId = setTimeout(run, 2500);

  return () => {
    window.removeEventListener('pageshow', run);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    clearTimeout(timeoutId);
  };
}

function AnimatedFollowerCount({ count, active }) {
  const targetCount = getFollowerDisplayCount(count);
  const [displayCount, setDisplayCount] = useState(targetCount);
  const finalNumber = formatFollowerDisplayCount(count);
  const currentNumber = formatFollowerDisplayCount(displayCount);
  const finalLabel = `${finalNumber} Spotify followers`;

  useEffect(() => {
    if (!active) {
      setDisplayCount(targetCount);
      return undefined;
    }

    let frameId;
    const duration = 2000;
    const startedAt = performance.now();

    setDisplayCount(0);

    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayCount(Math.round(targetCount * eased));

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setDisplayCount(targetCount);
      }
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [active, targetCount]);

  return (
    <div className="playlist-card__followers" aria-label={finalLabel}>
      <data value={targetCount}>
        <span className="playlist-card__followers-number">
          <span className="playlist-card__followers-number-ghost" aria-hidden="true">
            {finalNumber}
          </span>
          <span className="playlist-card__followers-number-current">
            {currentNumber}
          </span>
        </span>
        <span className="playlist-card__followers-label"> followers</span>
        <span className="playlist-card__followers-seo">{finalLabel}</span>
      </data>
    </div>
  );
}

function ResilientPlaylistPlayer({ playlist }) {
  const [attempt, setAttempt] = useState(0);
  const [iframeSrc, setIframeSrc] = useState('');
  const loadTimeoutRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    setAttempt(0);
  }, [playlist.embedSrc]);

  useEffect(() => {
    let cancelled = false;
    let frameId = null;
    loadedRef.current = false;
    setIframeSrc('');

    const clearLoadTimeout = () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };

    clearLoadTimeout();

    const cancelVisibleWait = runWhenPageVisible(() => {
      if (cancelled) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }

        setIframeSrc(buildPlaylistEmbedSrc(playlist.embedSrc, attempt));
        loadTimeoutRef.current = setTimeout(() => {
          if (!loadedRef.current && attempt < PLAYLIST_PLAYER_MAX_RETRIES) {
            setAttempt((currentAttempt) => Math.min(currentAttempt + 1, PLAYLIST_PLAYER_MAX_RETRIES));
          }
        }, PLAYLIST_PLAYER_LOAD_TIMEOUT_MS + attempt * 900);
      });
    });

    return () => {
      cancelled = true;
      cancelVisibleWait();
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      clearLoadTimeout();
    };
  }, [attempt, playlist.embedSrc]);

  const handleLoad = () => {
    loadedRef.current = true;
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  };

  const handleError = () => {
    if (attempt < PLAYLIST_PLAYER_MAX_RETRIES) {
      setAttempt((currentAttempt) => Math.min(currentAttempt + 1, PLAYLIST_PLAYER_MAX_RETRIES));
    }
  };

  return (
    <div className={`playlist-card__player playlist-fade-target ${iframeSrc ? '' : 'playlist-card__player--loading'}`.trim()}>
      {iframeSrc ? (
        <iframe
          key={`${playlist.id}-${attempt}`}
          src={iframeSrc}
          width="100%"
          height="352"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          title={`${playlist.name} playlist player`}
          onLoad={handleLoad}
          onError={handleError}
        ></iframe>
      ) : (
        <span className="playlist-card__player-status">Loading playlist player...</span>
      )}
    </div>
  );
}

function scrollExpandedPlaylistIntoView(playlistId) {
  if (typeof window === 'undefined') return;

  const detailsEl = document.getElementById(`${playlistId}-details`);
  const card = detailsEl ? detailsEl.closest('.playlist-card') : null;
  if (!card) return;

  const trigger = card.querySelector('.playlist-card__header');
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const margin = Math.min(28, Math.max(14, viewportHeight * 0.04));
  const anchorRect = trigger ? trigger.getBoundingClientRect() : card.getBoundingClientRect();
  const nextTop = anchorRect.top + scrollY - margin;

  window.scrollTo({
    top: Math.max(0, nextTop),
    behavior: 'smooth',
  });
}

const PlaylistsPage = () => {
  const { hideInstagram } = useRegionalVisibility();
  const [expandedId, setExpandedId] = useState(null);
  const [currentVisualIndex] = useState(() => getInitialLogoVisualIndexForPage('/playlists'));
  const osciCanvasRef = useRef(null);
  const osciAnimatorRef = useRef(null);
  const osciSvgListRef = useRef([]);
  const [osciReady, setOsciReady] = useState(false);
  const currentVisual = LOGO_SEQUENCE[currentVisualIndex] ?? LOGO_SEQUENCE[0];

  useEffect(() => {
    const cleanups = [];
    let isMounted = true;

    const startEffects = () => {
      if (!isMounted) {
        return;
      }

      const decodeNodes = document.querySelectorAll('[data-decode]');
      const flickerNodes = document.querySelectorAll('.flicker-text');

      decodeNodes.forEach((node) => {
        const text = node.getAttribute('data-decode') || node.innerText;
        cleanups.push(decodeTextLocked(node, text));
      });

      flickerNodes.forEach((node) => {
        cleanups.push(applyRandomFlicker(node, 50, 200, 1, 0.7));
      });
    };

    const cancelFontWait = runAfterFontsReady(startEffects);

    return () => {
      isMounted = false;
      cancelFontWait();
      cleanups.forEach((cleanup) => cleanup && cleanup());
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadSvgAssets = async () => {
      try {
        const svgUrls = OSCI_SVG_ASSET_URLS;

        if (!svgUrls.length) {
          console.error('No SVG files found in ./svg for oscii');
          return;
        }

        const svgTexts = await Promise.all(svgUrls.map((url) => fetch(url).then((res) => res.text())));
        if (isMounted) {
          osciSvgListRef.current = svgTexts;
          setOsciReady(true);
        }
      } catch (error) {
        console.error('Failed to load osci SVG assets for playlists', error);
      }
    };

    loadSvgAssets();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const visual = LOGO_SEQUENCE[currentVisualIndex];
    if (visual.type !== 'osci') {
      if (osciAnimatorRef.current) {
        osciAnimatorRef.current.destroy();
        osciAnimatorRef.current = null;
      }
      return undefined;
    }
    if (!osciReady || !window.OsciLogoAnimator) {
      return undefined;
    }

    const canvas = osciCanvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const animator = new window.OsciLogoAnimator({
      canvas,
      svgList: osciSvgListRef.current.length ? osciSvgListRef.current : undefined,
      svgIndex: 0,
      cycleOnRelock: true,
      direction: -1,
      beams: 3,
      transparent: true,
      trailFade: 0.1,
      background: 'rgba(0,0,0,0)',
      beamColor: '#ff0000',
      baseOutlineColor: '#0011ff',
      scan: false,
      vig: 0,
      rgbAberration: 20,
      speed: 1.55,
      speedBoost: 0.5,
      intensity: 2.2,
      highlightWidthPx: 60,
      maxFps: 60,
      renderScale: 0.99,
      quality: 1.0,
      adaptive: false,
      traceStepPx: 5,
      strokePx: 0.5,
      glowPx: 0,
      miterLimit: 14,
      baseOutlineAlpha: 0.1,
      baseOutlineStrokePx: 0.1,
      frameJitter: 0,
      stableJitterPx: 0,
      lostJitterPx: 0,
      dropout: 0.3,
      signalEnabled: true,
      signalNoise: 1,
      signalHoldMin: 3.2,
      signalHoldMax: 4.4,
      signalDropMin: 0.77,
      signalDropMax: 2.28,
      glitch: true,
      glitchChance: 0.37,
    });

    osciAnimatorRef.current = animator;
    animator.start();

    return () => {
      animator.destroy();
      osciAnimatorRef.current = null;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [currentVisualIndex, osciReady]);

  useEffect(() => {
    if (!expandedId) {
      return undefined;
    }

    const iconContainer = document.querySelector(`[data-playlist-icons="${expandedId}"]`);
    const detailsEl = document.getElementById(`${expandedId}-details`);
    const icons = iconContainer ? Array.from(iconContainer.querySelectorAll('img')) : [];
    const timers = [];

    icons.forEach((icon) => {
      icon.style.opacity = '0';
      const randomDelay = Math.random() * 0.5;
      const delayId = setTimeout(() => {
        const blinkInterval = setInterval(() => {
          icon.style.opacity = Math.random() > 0.5 ? '1' : '0';
        }, 50);
        timers.push({ type: 'interval', id: blinkInterval });

        const settleId = setTimeout(() => {
          clearInterval(blinkInterval);
          icon.style.removeProperty('opacity');
        }, 1000);
        timers.push({ type: 'timeout', id: settleId });
      }, randomDelay * 1000);
      timers.push({ type: 'timeout', id: delayId });
    });

    const fadeTargets = detailsEl ? Array.from(detailsEl.querySelectorAll('.playlist-fade-target')) : [];
    fadeTargets.forEach((node, index) => {
      node.classList.remove('playlist-fade');
      void node.offsetWidth; // restart animation
      node.style.animationDelay = `${index * 0.08}s`;
      node.classList.add('playlist-fade');
    });

    return () => {
      timers.forEach((timer) => {
        if (timer.type === 'interval') {
          clearInterval(timer.id);
        } else {
          clearTimeout(timer.id);
        }
      });
      icons.forEach((icon) => icon.style.removeProperty('opacity'));
      fadeTargets.forEach((node) => {
        node.classList.remove('playlist-fade');
        node.style.removeProperty('animation-delay');
      });
    };
  }, [expandedId]);

  useEffect(() => {
    if (!expandedId || typeof window === 'undefined') {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      scrollExpandedPlaylistIntoView(expandedId);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [expandedId]);

  const toggleCard = (id) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id;
      if (next) {
        const playlist = PLAYLISTS.find((item) => item.id === next);
        trackSiteEvent('playlist_card_opened', {
          source_page: 'playlists',
          playlist_id: playlist?.id || next,
          playlist_name: playlist?.name || '',
        });
      }
      return next;
    });
  };

  return (
    <div className="App playlists-page">
      <div id="background">
        <ResponsiveDecodedBackground mobileSrc={mobileNoise} tabletSrc={tabletNoise} desktopSrc={desktopNoise} />
        <img src={DEFAULT_HERO_GIF} alt="" aria-hidden="true" id="animated-gif" />
      </div>

      <div id="content-wrapper" className="playlists-wrapper">
        <div className="playlists-panel">
          <a href="/" className="logo-home-link" aria-label="Back to home">
            {currentVisual.type === 'image' ? (
              <DecodedArtImage
                src={currentVisual.src}
                alt="IVA Gonzo logo"
                id="background-gif"
                aspectRatio={currentVisual.aspectRatio}
              />
            ) : (
              <div id="background-gif" className="osci-wrap">
                {osciReady ? (
                  <canvas id="osci-playlists" ref={osciCanvasRef} />
                ) : null}
              </div>
            )}
          </a>

          <header className="playlists-header">
            <p className="header-eyebrow">
              CURATED BY{' '}
              {hideInstagram ? (
                <span>IVA GONZO</span>
              ) : (
                <a
                  href="https://www.instagram.com/ivagonzo/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  IVA GONZO
                </a>
              )}
            </p>
            <h1
              className="page-title flicker-text no-fade"
              data-decode="NOT JUST PLAYLISTS"
            >
              NOT JUST PLAYLISTS
            </h1>
            <p className="page-subtitle">EARLY ACCESS TO MUSIC BEFORE THE HYPE</p>
            <p className="page-description">
              Powered by endless digging in new releases, private drops and underground scenes, these dark, next-wave
              mood playlists are for people who want to hear what’s coming next, not what’s already everywhere.
            </p>
          </header>

          <section className="playlists-shelf">
            <div className="playlists-grid">
              {PLAYLISTS.map((playlist, playlistIndex) => {
                const isExpanded = expandedId === playlist.id;
                const combinedTitle = `${playlist.emoji} ${playlist.name}`;
                const emojiDecodeOffset = PLAYLIST_EMOJI_DECODE_OFFSETS[playlistIndex] || 0;

                return (
                  <article
                    key={playlist.id}
                    className={`playlist-card ${isExpanded ? 'active' : ''}`}
                  >
                    <button
                      className="playlist-card__header"
                      onClick={() => toggleCard(playlist.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`${playlist.id}-details`}
                    >
                      <div className="playlist-card__title-group">
                        <span className="playlist-card__vibe">{playlist.vibe}</span>
                        <span
                          className={`playlist-card__title flicker-text ${isExpanded ? 'active-title' : ''}`}
                          data-decode={combinedTitle}
                          data-decode-emoji-offset={emojiDecodeOffset}
                        >
                          {combinedTitle}
                        </span>
                      </div>
                      <span className="playlist-card__toggle" aria-hidden="true">
                        {isExpanded ? '−' : '+'}
                      </span>
                    </button>

                    <div
                      className={`playlist-card__details ${isExpanded ? '' : 'playlist-card__details--collapsed'}`.trim()}
                      id={`${playlist.id}-details`}
                      aria-hidden={!isExpanded}
                    >
                      <AnimatedFollowerCount count={playlist.followers} active={isExpanded} />
                      <p className="playlist-card__tagline playlist-fade-target">{playlist.tagline}</p>
                      <ul className="playlist-card__facts">
                        {playlist.info.map((item) => (
                          <li key={item.label} className="playlist-fade-target">
                            <span>{item.label}</span>
                            <p>{item.text}</p>
                          </li>
                        ))}
                      </ul>
                      <div className="playlist-card__listen playlist-fade-target">
                        <span>Listen on</span>
                        <div
                          className="playlist-card__streaming-links"
                          data-playlist-icons={playlist.id}
                        >
                          {playlist.streaming.map((link) => {
                            const meta = STREAMING_META[link.type];
                            return (
                              <a
                                key={link.type}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={meta.label}
                                tabIndex={isExpanded ? 0 : -1}
                              >
                                <img src={meta.icon} alt={meta.label} />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                      {isExpanded && (
                        <>
                          <ResilientPlaylistPlayer playlist={playlist} />
                          <p className="playlist-card__cta playlist-fade-target">
                            <a
                              href={PUBLIC_SUBMISSION_URL}
                              target={PUBLIC_SUBMISSION_TARGET}
                              rel={PUBLIC_SUBMISSION_REL}
                              className="playlist-card__submit-link"
                              onClick={(event) =>
                                handleSubmitMusicIntentClick(event, {
                                  source_page: 'playlists',
                                  cta_location: 'playlist_card',
                                  playlist_id: playlist.id,
                                  playlist_name: playlist.name,
                                  destination: PUBLIC_SUBMISSION_URL,
                                })
                              }
                            >
                              <span className="btn-glass" aria-hidden="true"></span>
                              <span className="playlist-card__submit-label">SUBMIT MY TRACK</span>
                            </a>
                          </p>
                        </>
                      )}
                    </div>
                </article>
              );
            })}
            </div>
          </section>

          <section className="support-section support-submit-cta-section playlists-submit-cta-section">
            <article className="support-submit-cta playlists-submit-cta">
              <span className="support-submit-cta__eyebrow">Submission info</span>
              <p className="support-submit-cta__copy support-submit-cta__copy--secondary">
                If you want to submit music to an independent Spotify playlist curator for review and possible playlist consideration, visit the{' '}
                <a
                  className="support-submit-cta__inline-link"
                  href={PUBLIC_SUBMISSION_URL}
                  target={PUBLIC_SUBMISSION_TARGET}
                  rel={PUBLIC_SUBMISSION_REL}
                  onClick={(event) =>
                    handleSubmitMusicIntentClick(event, {
                      source_page: 'playlists',
                      cta_location: 'submission_info_inline_link',
                      destination: PUBLIC_SUBMISSION_URL,
                    })
                  }
                >
                  Submit Your Music
                </a>{' '}
                page.
              </p>
              <div className="support-submit-cta__actions">
                <a
                  href={PUBLIC_SUBMISSION_URL}
                  target={PUBLIC_SUBMISSION_TARGET}
                  rel={PUBLIC_SUBMISSION_REL}
                  className="support-submit-cta__link"
                  onClick={(event) =>
                    handleSubmitMusicIntentClick(event, {
                      source_page: 'playlists',
                      cta_location: 'submission_info_button',
                      destination: PUBLIC_SUBMISSION_URL,
                    })
                  }
                >
                  <span className="btn-glass" aria-hidden="true"></span>
                  <span className="support-submit-cta__label">SUBMIT YOUR MUSIC</span>
                </a>
              </div>
            </article>
          </section>

          {!SUBMITHUB_DIRECT_TEMPORARY_MODE && (
            <section className="support-section support-submit-cta-section playlists-support-cta-section">
              <article className="support-submit-cta playlists-support-cta">
                <span className="support-submit-cta__eyebrow playlists-support-cta__eyebrow">Support</span>
                <p className="support-submit-cta__copy">
                  If these playlists matter to you, this is how you help keep them independent, updated, and alive.
                </p>
                <p className="support-submit-cta__copy support-submit-cta__copy--secondary">
                  Your support helps me grow the playlists, spotlight overlooked artists and producers, and keep pushing
                  the dark alternative aesthetic and underexposed sounds that deserve more attention.
                </p>
                <div className="support-submit-cta__actions">
                  <a href="/support" className="support-submit-cta__link playlists-support-cta__link">
                    <span className="btn-glass" aria-hidden="true"></span>
                    <span className="support-submit-cta__label playlists-support-cta__label">SUPPORT THE WORK</span>
                  </a>
                </div>
              </article>
            </section>
          )}
        </div>

        <footer id="social-icons">
          <a href="/" aria-label="Back to home">
            <img className="social-icon" src={homeIcon} alt="Home" />
          </a>
          {!hideInstagram && (
            <a href="https://www.instagram.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
              <img className="social-icon" src={instagramIcon} alt="Instagram" />
            </a>
          )}
          <a href="https://open.spotify.com/user/11185646448?si=e1a94395b1054a48" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={spotifyProfileIcon} alt="Spotify" />
          </a>
          <a href="https://youtube.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={youtubeSocialIcon} alt="YouTube" />
          </a>
          <a href="https://www.pinterest.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={pinterestIcon} alt="Pinterest" />
          </a>
        </footer>
      </div>
    </div>
  );
};

export default PlaylistsPage;
